import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { ContainerWizardComponent } from 'src/app/project/component/container-wizard/container-wizard.component';
import { ContainerType } from 'src/app/shared/models/container.types';
import { SortBy } from 'src/app/shared/enums/sortby.enum';

@Component({
    selector: 'app-container-overview',
    templateUrl: './container-overview.component.html',
    styleUrls: ['./container-overview.component.scss'],
    standalone: false,
})
export class ContainerOverviewComponent implements OnInit, OnDestroy {
    @Input() type: 'motoric' | 'visual' | 'imageDb';
    largeHeadline: string;
    smallHeadlineTranslationKey: string;
    headerPicture: string;
    createButtonTranslationKey: string;
    searchTerm: string = '';
    subscriptions: Subscription[] = [];
    resetSearchTrigger$: Subject<void> = new Subject<void>();

    constructor(
        private authenticationService: AuthenticationService,
        private router: Router,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private modal: NgbModal
    ) {}

    ngOnInit() {
        if (
            !(
                this.authenticationService.currentUserValue.roles.includes(
                    'ROLE_USER_MOTORIC_TRAINING'
                ) ||
                this.authenticationService.currentUserValue.roles.includes(
                    'ROLE_USER_VISUAL_TRAINING'
                )
            )
        ) {
            this.router.navigate(['/communityprojects']);
            return;
        }
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                this.type = params['type'];
                if (
                    this.type === 'motoric' &&
                    !this.authenticationService.currentUserValue.roles.includes(
                        'ROLE_USER_MOTORIC_TRAINING'
                    )
                ) {
                    this.router.navigate(['/workspace/visual']);
                    return;
                } else if (
                    (this.type === 'visual' || this.type === 'imageDb') &&
                    !this.authenticationService.currentUserValue.roles.includes(
                        'ROLE_USER_VISUAL_TRAINING'
                    )
                ) {
                    this.router.navigate(['/workspace/motoric']);
                    return;
                }
                this.largeHeadline = this.translateService.instant(
                    'workspace.components.containerOverview.' +
                        this.type +
                        'LargeHeadline'
                );
                this.smallHeadlineTranslationKey =
                    'workspace.components.containerOverview.' +
                    this.type +
                    'SmallHeadline';
                switch (this.type) {
                    case 'motoric':
                        this.headerPicture =
                            'assets/svg/sidebar-nav/motoric-icon.svg';
                        this.createButtonTranslationKey =
                            'workspace.components.containerOverview.createProjectButton';
                        break;
                    case 'visual':
                        this.headerPicture =
                            'assets/svg/sidebar-nav/visual-icon.svg';
                        this.createButtonTranslationKey =
                            'workspace.components.containerOverview.createProjectButton';
                        break;
                    case 'imageDb':
                        this.headerPicture =
                            'assets/svg/sidebar-nav/picdatabase-icon.svg';
                        this.createButtonTranslationKey =
                            'workspace.components.containerOverview.createImageDbButton';
                        break;
                }
            })
        );
    }

    onOpenCreationWizard() {
        let modalRef = this.modal.open(ContainerWizardComponent, {
            size: 'lg',
            modalDialogClass: 'add-project-wizard-modal',
        });
        switch (this.type) {
            case 'imageDb':
                modalRef.componentInstance.type = ContainerType.IMAGE_DB;
                break;
            case 'motoric':
                modalRef.componentInstance.type = ContainerType.PROJECT_MOTORIC;
                break;
            case 'visual':
                modalRef.componentInstance.type = ContainerType.PROJECT_VISUAL;
                break;
        }
    }

    onSearchChange(event: {
        searchText: string;
        sortOption: { sortBy: SortBy; asc: boolean };
    }): void {
        this.searchTerm = event.searchText;
        this.navigateWithFilters(this.searchTerm, event.sortOption);
    }

    onSearchCleared(): void {
        this.resetSearchTrigger$.next();
    }

    private navigateWithFilters(
        searchText: string,
        sortOption: { sortBy: SortBy; asc: boolean }
    ): void {
        this.router.navigate(['workspace/' + this.type], {
            queryParams: {
                sortBy: sortOption.sortBy,
                asc: sortOption.asc,
                searchText: searchText,
            },
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) =>
            subscription.unsubscribe()
        );
        this.resetSearchTrigger$.complete();
    }
}
